import { trackBookingStepEvent } from "./app-insights-telemetry";
import { emitEvent } from "./emit-event";
import { EVENT_TYPE } from "./events";

// Mirrors a booking step to Application Insights alongside the GTM/Jentis event.
const trackStepInAppInsights = (data: Record<string, unknown>): void => {
  trackBookingStepEvent({
    flow: "Vetstoria",
    step: String(data.bookingStepName),
    stepNumber: data.bookingStepNumber as string | number | undefined,
    complete: Boolean(data.bookingStepComplete),
    bookingSessionId: data.bookingSessionId as string | undefined,
    clinicId: data.clinicCmsId as string | undefined,
    clinicPmsId: data.clinicPmsId as string | undefined
  });
};

(() => {
  // Vetstoria calls history.pushState() for each new booking step.
  // Unfortunately, calling history.pushState() triggers no listenable event at all normally.
  // So we have to monkey patch the function here so we can listen for it.
  const pushState = window.history.pushState;

  window.history.pushState = function (...args) {
    const result = pushState.apply(window.history, args);

    window.dispatchEvent(new Event("pushstate"));

    return result;
  };

  // Steps that are considered complete as soon as they are reached and should never be re-emitted as completed
  const immediatelyCompletedSteps = ["Confirmed"];

  window.addEventListener("pushstate", _ => {
    let stepName: string | undefined;
    let stepNumber: string | undefined;

    // Clear up Vetstoria prefixes from the hash to determine current step
    // nc - New client
    // rc - Returning client
    // xc - Unknown client
    const hash = window.location.hash
      .replace("vs-nc-", "")
      .replace("vs-rc-", "")
      .replace("vs-xc-", "");

    switch (hash) {
      case "#branch":
        stepName = "SelectClinic";
        stepNumber = "1";
        break;
      case "#appointment-type":
        stepName = "SelectTreatment";
        stepNumber = "2";
        break;
      case "#slots":
        stepName = "SelectDateAndTime";
        stepNumber = "3";
        break;
      case "#pet-owner-details":
        stepName = "ContactDetails";
        stepNumber = "4";
        break;
      case "#ncf-pet-info":
        stepName = "PetDetails";
        stepNumber = "5";
        break;
      case "#confirm":
        stepName = "Confirmation";
        stepNumber = "5b";
        break;
      case "#completed":
        stepName = "Confirmed";
        stepNumber = "6";
        break;
    }

    if (stepName && stepNumber && window.ani_vetstoriaData?.bookingData) {
      const { bookingData, previousEventData } = window.ani_vetstoriaData;

      // The vetstoria widget has no way to properly de-initialize, so we get duplicate events if
      // the user closes and reopens the booking modal. So we make sure to only track events that
      // are from a new booking flow (different sessionId), or a different step.
      if (
        !previousEventData ||
        bookingData.sessionId !== previousEventData.bookingSessionId ||
        stepName !== previousEventData.bookingStepName
      ) {
        const eventData = {
          action: stepName,
          bookingSessionId: bookingData.sessionId,
          bookingStepComplete: immediatelyCompletedSteps.includes(stepName),
          bookingStepName: stepName,
          bookingStepNumber: stepNumber,
          category: "vetstoria_online_booking",
          clinicBId: bookingData.bId,
          clinicCmsId: bookingData.id,
          clinicName: bookingData.name,
          clinicPmsId: bookingData.vetstoriaId,
          label: `Step ${stepNumber} - ${stepName}`
        };

        if (
          previousEventData &&
          eventData.bookingSessionId === previousEventData.bookingSessionId
        ) {
          if (
            !immediatelyCompletedSteps.includes(
              previousEventData.bookingStepName as string
            )
          ) {
            // Emit the previous step as "completed"
            const completedPrevious = { ...previousEventData, bookingStepComplete: true };

            emitEvent(EVENT_TYPE.onlineBooking, completedPrevious, false);
            trackStepInAppInsights(completedPrevious);
          }
        } else if (stepName === "SelectTreatment") {
          // Special case for booking modals which don't have the #branch step (they're just for a single clinic).
          // Those begin with the SelectTreatment step, so we need to emit a completed SelectClinic step first.
          const selectClinicCompleted = {
            ...eventData,
            action: "SelectClinic",
            bookingStepComplete: true,
            bookingStepName: "SelectClinic",
            bookingStepNumber: 1,
            label: "Step 1 - SelectClinic"
          };

          emitEvent(EVENT_TYPE.onlineBooking, selectClinicCompleted, false);
          trackStepInAppInsights(selectClinicCompleted);
        }

        emitEvent(EVENT_TYPE.onlineBooking, eventData, false);
        trackStepInAppInsights(eventData);

        window.ani_vetstoriaData.previousEventData = eventData;
      }
    }
  });
})();
